import type { ReactElement } from 'react';
import { Button } from '@/shared/ui';
import { useRequestPasswordReset } from '../model/use-password-reset';

interface PasswordChangeRequestProps {
  /** 로그인한 회원의 이메일 — 재설정 메일을 이 주소로 보낸다. */
  readonly email: string;
  /** 이메일 인증을 마쳤는지 — 서버는 인증된 이메일로만 재설정 메일을 보내므로, 미인증이면 버튼 대신 안내만 둔다. */
  readonly emailVerified: boolean;
}

const CONFIRM_MESSAGE = '비밀번호 재설정 메일을 보낼까요? 메일의 링크에서 새 비밀번호를 정합니다.';

/**
 * 로그인한 회원의 비밀번호 변경 — 별도 변경 API 가 없어 기존 재설정 메일 흐름(auth.requestPasswordReset)을 쓴다.
 * 확인 창을 거친 뒤 보내고, 보낸 뒤에는 안내 문구로 바뀐다. 이메일 인증 전이면 메일이 오지 않으므로 먼저 인증하라고 안내한다.
 */
export const PasswordChangeRequest = ({
  email,
  emailVerified,
}: PasswordChangeRequestProps): ReactElement => {
  const { emailError, formError, isPending, isDone, submit } = useRequestPasswordReset();

  if (!emailVerified) {
    return (
      <p className="text-sm text-muted-foreground">
        이메일 인증을 마친 뒤 비밀번호를 바꿀 수 있어요. 위 인증 상태에서 이메일 인증을 먼저 해
        주세요.
      </p>
    );
  }

  if (isDone) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        <span className="font-semibold text-foreground">{email}</span> 로 재설정 메일을 보냈습니다.
        메일의 링크에서 새 비밀번호를 설정해 주세요.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        variant="outline"
        size="lg"
        isLoading={isPending}
        className="h-10 w-fit border-foreground/20 px-6"
        onClick={() => {
          if (window.confirm(CONFIRM_MESSAGE)) void submit(email);
        }}
      >
        비밀번호 재설정 메일 받기
      </Button>
      {emailError || formError ? (
        <p role="alert" className="text-sm text-destructive">
          {emailError ?? formError}
        </p>
      ) : null}
    </div>
  );
};
